import React, { useRef, useState } from 'react';
import {
  UploadCloud,
  FileCheck,
  FileSpreadsheet,
  Download,
  Play,
  RotateCcw,
  Sparkles,
  Layers,
  ArrowRight,
} from 'lucide-react';
import { useReconciliation } from '../../context/ReconciliationContext';
import { parseSpreadsheetFile, detectRazorpayColumns, detectErpColumns } from '../../engine/parser';
import { downloadSampleExcel } from '../../engine/sampleData';

interface FileUploaderProps {
  onOpenMapping: () => void;
  onViewTable: () => void;
}

export const FileUploader: React.FC<FileUploaderProps> = ({ onOpenMapping, onViewTable }) => {
  const {
    razorpayInfo,
    erpInfo,
    setRazorpayData,
    setErpData,
    runReconciliation,
    isProcessing,
    isReconciled,
    summary,
    resetAll,
    loadDemoData,
  } = useReconciliation();

  const rpInputRef = useRef<HTMLInputElement>(null);
  const erpInputRef = useRef<HTMLInputElement>(null);

  const [rpDragOver, setRpDragOver] = useState(false);
  const [erpDragOver, setErpDragOver] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleFileUpload = async (file: File, type: 'razorpay' | 'erp') => {
    setErrorMessage(null);
    try {
      const parsed = await parseSpreadsheetFile(file);

      if (type === 'razorpay') {
        const autoDetected = detectRazorpayColumns(parsed.headers);
        setRazorpayData(parsed, autoDetected);
      } else {
        const autoDetected = detectErpColumns(parsed.headers);
        setErpData(parsed, autoDetected);
      }
    } catch (err: any) {
      setErrorMessage(`Error parsing ${file.name}: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header instructions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <h2 className="text-base font-bold text-slate-900 dark:text-white">
            Dual File Ingestion & Aggregation Hub
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Upload Scheme App Razorpay transaction reports and ERP multi-bill billing reports (.xlsx, .xls, .csv).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadSampleExcel('razorpay')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition"
            title="Download formatted sample Razorpay file"
          >
            <Download className="w-3.5 h-3.5 text-brand-600" />
            <span>Sample Razorpay</span>
          </button>
          <button
            onClick={() => downloadSampleExcel('erp')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition"
            title="Download formatted sample ERP file with 1-to-many split bills"
          >
            <Download className="w-3.5 h-3.5 text-emerald-600" />
            <span>Sample ERP</span>
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-xs flex items-center justify-between">
          <span>{errorMessage}</span>
          <button onClick={() => setErrorMessage(null)} className="font-bold underline ml-3">
            Dismiss
          </button>
        </div>
      )}

      {/* Dual Upload Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Razorpay Report Card */}
        <div
          className={`relative border-2 border-dashed rounded-2xl p-6 transition flex flex-col items-center justify-center text-center ${
            rpDragOver
              ? 'border-brand-500 bg-brand-50/50 dark:bg-brand-950/20'
              : razorpayInfo
              ? 'border-emerald-300 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-950/10'
              : 'border-slate-300 dark:border-slate-700 hover:border-brand-400 bg-white dark:bg-slate-900'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setRpDragOver(true);
          }}
          onDragLeave={() => setRpDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setRpDragOver(false);
            if (e.dataTransfer.files[0]) handleFileUpload(e.dataTransfer.files[0], 'razorpay');
          }}
        >
          <input
            ref={rpInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.[0]) handleFileUpload(e.target.files[0], 'razorpay');
            }}
          />

          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3.5 ${
              razorpayInfo
                ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-300'
                : 'bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400'
            }`}
          >
            {razorpayInfo ? <FileCheck className="w-7 h-7" /> : <UploadCloud className="w-7 h-7" />}
          </div>

          <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
            1. Razorpay Payment Report
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xs">
            Exported from Razorpay Dashboard with Payment IDs, amounts, customer details, and refund status.
          </p>

          {razorpayInfo ? (
            <div className="mt-4 w-full bg-white dark:bg-slate-800 rounded-xl p-3 border border-emerald-200 dark:border-emerald-900 text-left">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[200px]">
                  {razorpayInfo.fileName}
                </span>
                <span className="font-mono text-emerald-600 dark:text-emerald-400 font-bold">
                  {razorpayInfo.rowCount} records
                </span>
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
                <span>{razorpayInfo.headers.length} detected columns</span>
                <span>&bull;</span>
                <button
                  onClick={() => rpInputRef.current?.click()}
                  className="text-brand-600 dark:text-brand-400 hover:underline font-medium"
                >
                  Change File
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => rpInputRef.current?.click()}
                className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-medium text-xs shadow-sm transition"
              >
                Select Razorpay File
              </button>
              <div className="text-[11px] text-slate-400 mt-2">or drag and drop file here</div>
            </div>
          )}
        </div>

        {/* ERP Billing Report Card */}
        <div
          className={`relative border-2 border-dashed rounded-2xl p-6 transition flex flex-col items-center justify-center text-center ${
            erpDragOver
              ? 'border-brand-500 bg-brand-50/50 dark:bg-brand-950/20'
              : erpInfo
              ? 'border-emerald-300 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-950/10'
              : 'border-slate-300 dark:border-slate-700 hover:border-brand-400 bg-white dark:bg-slate-900'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setErpDragOver(true);
          }}
          onDragLeave={() => setErpDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setErpDragOver(false);
            if (e.dataTransfer.files[0]) handleFileUpload(e.dataTransfer.files[0], 'erp');
          }}
        >
          <input
            ref={erpInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.[0]) handleFileUpload(e.target.files[0], 'erp');
            }}
          />

          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3.5 ${
              erpInfo
                ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-300'
                : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400'
            }`}
          >
            {erpInfo ? <FileCheck className="w-7 h-7" /> : <FileSpreadsheet className="w-7 h-7" />}
          </div>

          <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
            2. ERP Billing Report
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xs">
            Exported from SAP / Zoho Books / Tally Prime with Payment IDs and split bill numbers.
          </p>

          {erpInfo ? (
            <div className="mt-4 w-full bg-white dark:bg-slate-800 rounded-xl p-3 border border-emerald-200 dark:border-emerald-900 text-left">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[200px]">
                  {erpInfo.fileName}
                </span>
                <span className="font-mono text-emerald-600 dark:text-emerald-400 font-bold">
                  {erpInfo.rowCount} records
                </span>
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
                <span>{erpInfo.headers.length} detected columns</span>
                <span>&bull;</span>
                <button
                  onClick={() => erpInputRef.current?.click()}
                  className="text-brand-600 dark:text-brand-400 hover:underline font-medium"
                >
                  Change File
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => erpInputRef.current?.click()}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-medium text-xs shadow-sm transition"
              >
                Select ERP File
              </button>
              <div className="text-[11px] text-slate-400 mt-2">or drag and drop file here</div>
            </div>
          )}
        </div>
      </div>

      {/* Critical Business Rule Reminder Notice */}
      <div className="p-4 rounded-xl bg-blue-50/60 dark:bg-slate-800/80 border border-blue-200/80 dark:border-slate-700 flex items-start gap-3">
        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300">
          <Layers className="w-4 h-4" />
        </div>
        <div className="text-xs text-slate-600 dark:text-slate-300">
          <span className="font-semibold text-slate-900 dark:text-white">
            1-to-Many Bill Aggregation Engine Active:
          </span>{' '}
          When a customer's single Razorpay Payment ID is billed across multiple ERP invoices (e.g. ₹4,000 + ₹3,000 + ₹3,000 = ₹10,000),
          the engine automatically groups the ERP bills by Payment ID, calculates total sum, and performs precise exact/variance matching.
        </div>
      </div>

      {/* Action Controls Bar */}
      <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={onOpenMapping}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs font-semibold shadow-sm transition"
          >
            <Sparkles className="w-4 h-4 text-brand-500" />
            <span>Column Mapping Wizard</span>
          </button>

          <button
            onClick={loadDemoData}
            disabled={isProcessing}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/30 hover:bg-amber-100 text-amber-800 dark:text-amber-300 text-xs font-semibold transition disabled:opacity-50"
          >
            <span>Load Sample Data</span>
          </button>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          {(razorpayInfo || erpInfo) && (
            <button
              onClick={resetAll}
              className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
              title="Reset uploaded files"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}

          <button
            onClick={runReconciliation}
            disabled={isProcessing || !razorpayInfo || !erpInfo}
            className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-bold text-xs shadow-lg transition ${
              isProcessing || !razorpayInfo || !erpInfo
                ? 'bg-slate-200 text-slate-400 dark:bg-slate-800 dark:text-slate-600 cursor-not-allowed shadow-none'
                : 'bg-gradient-to-r from-brand-600 to-blue-600 hover:from-brand-700 hover:to-blue-700 text-white shadow-brand-500/25 active:scale-95'
            }`}
          >
            <Play className="w-4 h-4 fill-current" />
            <span>{isProcessing ? 'Reconciling Records...' : 'Run Reconciliation'}</span>
          </button>

          {isReconciled && (
            <button
              onClick={onViewTable}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition"
            >
              <span>View Table</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
